// @flow
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import AffordableWaitlistService from '../../../../../services/affordableWaitlistService';

export const ENABLE_WAITLIST_FIELD = 'setup.affordableWaitlist';

const affordableWaitlistService = new AffordableWaitlistService();

export function useApplicationsNeedingInfo(
  organizationId: string,
  propertyId: string,
  onChange: Function,
  editMode: boolean,
): {
  count: number,
  isOpen: boolean,
  handleEnableWaitlistChange: (evt: {
    target: { name: string, value: boolean },
  }) => Promise<void>,
  handleContinue: () => void,
  handleCancel: () => void,
} {
  const queryClient = useQueryClient();
  const [count, setCount] = useState(0);
  // Kept separate from count so the copy does not change while the modal animates closed.
  const [isOpen, setIsOpen] = useState(false);
  // Drops a response that lands after the user switched back off or left edit mode.
  const isEnablingRef = useRef(false);

  useEffect(() => {
    if (!editMode) {
      isEnablingRef.current = false;
      setIsOpen(false);
    }
  }, [editMode]);

  const handleEnableWaitlistChange = useCallback(
    async (evt) => {
      onChange(evt);
      isEnablingRef.current = Boolean(evt.target.value);
      if (!isEnablingRef.current) {
        setIsOpen(false);
        return;
      }

      try {
        const response = await queryClient.fetchQuery(
          ['applicationsNeedingInfo', organizationId, propertyId],
          () =>
            affordableWaitlistService.getApplicationsNeedingInfo(
              organizationId,
              propertyId,
            ),
          { staleTime: 0 },
        );
        if (isEnablingRef.current && (response?.count ?? 0) > 0) {
          setCount(response.count);
          setIsOpen(true);
        }
      } catch {
        // The check is informational; a failure must not block enabling.
      }
    },
    [onChange, queryClient, organizationId, propertyId],
  );

  const handleContinue = useCallback(() => setIsOpen(false), []);

  const handleCancel = useCallback(() => {
    isEnablingRef.current = false;
    setIsOpen(false);
    onChange({ target: { name: ENABLE_WAITLIST_FIELD, value: false } });
  }, [onChange]);

  return {
    count,
    isOpen,
    handleEnableWaitlistChange,
    handleContinue,
    handleCancel,
  };
}
